import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ModalBody, ModalHeader } from 'reactstrap';
import Rodal from '../Rodal/Rodal';
import './SeeMoreText.scss';

export interface SeeMoreTextProps {
    text: string;
    title?: string;
}

export const SeeMoreText = (props: SeeMoreTextProps) => {
    const textRef = useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);

    const checkTruncation = useCallback(() => {
        const element = textRef.current;
        if (!element) {
            return;
        }
        setIsTruncated(element.scrollWidth > element.clientWidth);
    }, []);

    useEffect(() => {
        checkTruncation();

        const element = textRef.current;
        if (!element) {
            return undefined;
        }

        if (typeof ResizeObserver !== 'undefined') {
            const resizeObserver = new ResizeObserver(() => checkTruncation());
            resizeObserver.observe(element);
            return () => resizeObserver.disconnect();
        }

        window.addEventListener('resize', checkTruncation);
        return () => window.removeEventListener('resize', checkTruncation);
    }, [checkTruncation, props.text]);

    return (
        <span className="see-more-text">
            <span ref={textRef} className="see-more-text-content">
                {props.text}
            </span>
            {isTruncated && (
                <button
                    type="button"
                    className="see-more-link"
                    onClick={() => setIsModalVisible(true)}>
                    See more
                </button>
            )}
            {isModalVisible &&
                createPortal(
                    <div className="see-more-text-modal-container">
                        <Rodal
                            visible={isModalVisible}
                            onClose={() => setIsModalVisible(false)}
                            animation="slideTop"
                            className="see-more-text-modal"
                            showMask={true}
                            width={640}
                            customStyles={{ height: 'auto' }}
                            showCloseButton={true}>
                            <ModalHeader>
                                <b>{props.title ?? 'Comment'}</b>
                            </ModalHeader>
                            <ModalBody>
                                <div className="see-more-text-modal-body">
                                    {props.text}
                                </div>
                                <div className="see-more-text-modal-buttons">
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => setIsModalVisible(false)}>
                                        Close
                                    </button>
                                </div>
                            </ModalBody>
                        </Rodal>
                    </div>,
                    document.body
                )}
        </span>
    );
};

export default SeeMoreText;
